import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { PageBlock, RichTextContent, Stack } from '@etg/wings';
import { DangerousHtmlContent } from '@eti/components';
import { useSnowplowTracking, useTranslation } from '@eti/providers';
import { SearchLocation } from '@eti/types';
import { connect } from 'react-redux';
import { change } from 'redux-form';
import { startOfTomorrow, addDays } from 'date-fns';
import { SEARCH } from '../../constants/formNamesConstants';
import {
  ORIGIN,
  DESTINATION,
  DEPARTURE_DATE,
  RETURN_DATE,
} from '../../widgets/search-form/constants/formFieldNames';
import { searchForm } from '../../widgets/search-form';
import SearchFormPanel from '../../common/search-form-panel/SearchFormPanel';
import { useJson } from '../../common/custom-hooks/json';
import { endpoints } from '../../constants/routesAndApisConstants';
import { StartPageDestinationPageContent } from '../../common/components/StartPageDestinationPageContent';
import { transformSearchParameters } from './utils/searchParams';

const { CARRIER_CODES, MULTI_BOUND, SINGLE_BOUND } = searchForm.formFieldNames;

const buildDestinationUrlParams = (
  originName,
  destinationName,
  textCode,
  carrierCode,
  countryCode,
  destinationTextId,
) => {
  const params = new URLSearchParams();

  params.append('originName', originName);
  params.append('destinationName', destinationName);
  params.append('textCode', textCode);
  if (carrierCode) {
    params.append('carrierCode', carrierCode);
  }
  if (destinationTextId) {
    params.append('destinationTextId', destinationTextId);
  }
  params.append('country', countryCode);

  return params.toString();
};

const DestinationPage = ({
  carrierCode,
  countryCode,
  changeValues,
  destination,
  destinationTextId,
  hasLogo,
  isRestricted,
  origin,
  setCarrierCode,
  setInitialDates,
  textCode,
}) => {
  const { t } = useTranslation();
  const setValueRef = useRef(null);
  const textUrlParams = buildDestinationUrlParams(
    origin?.name ?? '',
    destination?.name ?? '',
    textCode,
    carrierCode,
    countryCode,
    destinationTextId,
  );
  const textUrl = `${endpoints.DESTINATION_PAGE_GET_TEXTS}?${textUrlParams}`;
  const { data: destinationPageTexts } = useJson(textUrl);

  const destinationPageTitle = destinationPageTexts?.model?.destinationPageTitle;
  const carrierLogoUrl = destinationPageTexts?.model?.carrierLogoUrl;
  const carrierText = destinationPageTexts?.model?.carrierText;
  const destinationText = destinationPageTexts?.model?.destinationText;

  const { isSnowplowTrackingEnabled, sendTrackEvent, updateSnowplowContextData } =
    useSnowplowTracking();

  useEffect(() => {
    if (isSnowplowTrackingEnabled) {
      const destinationCode = destination?.iataCode ?? 'null';
      const originCode = origin?.iataCode ?? 'null';

      const newFlightData = {
        destinationAirportIatas: `${destinationCode}, ${originCode}`,
        originAirportIatas: `${originCode}, ${destinationCode}`,
      };

      updateSnowplowContextData({
        flightData: {
          ...newFlightData,
        },
      });

      sendTrackEvent({
        category: 'Destination',
        action: 'Seen',
        label: 'Page',
      });
    }
  }, [
    destination?.iataCode,
    isSnowplowTrackingEnabled,
    origin?.iataCode,
    sendTrackEvent,
    updateSnowplowContextData,
  ]);

  useEffect(() => {
    const transformedSearchParameters = transformSearchParameters(origin, destination);
    changeValues(transformedSearchParameters);

    const setValue = setValueRef?.current;
    if (setValue) {
      setValue(`${SINGLE_BOUND}.${ORIGIN}`, transformedSearchParameters.origin);
      setValue(`${SINGLE_BOUND}.${DESTINATION}`, transformedSearchParameters.destination);
      setValue(`${MULTI_BOUND}[0].${ORIGIN}`, transformedSearchParameters.origin);
      setValue(`${MULTI_BOUND}[0].${DESTINATION}`, transformedSearchParameters.destination);
    }
  }, [changeValues, origin, destination]);

  useEffect(() => {
    setInitialDates();
  }, [setInitialDates]);

  useEffect(() => {
    if (carrierCode && isRestricted) {
      setCarrierCode(carrierCode);
      const setValue = setValueRef?.current;
      if (setValue) {
        setValue(`${CARRIER_CODES}`, [carrierCode]);
      }
    }
  }, [carrierCode, isRestricted, setCarrierCode]);

  const panelHeaderTitle = destinationPageTitle ?? t('Destination.Title');

  return (
    <>
      <SearchFormPanel
        carrierLogoUrl={hasLogo ? carrierLogoUrl : undefined}
        destination={destination}
        origin={origin}
        panelHeaderTitle={panelHeaderTitle}
        setValueRef={setValueRef}
      />
      <StartPageDestinationPageContent destination={destination} origin={origin} />
      <PageBlock withPaddingY>
        <Stack spacing={24}>
          {destinationText && <RichTextContent>{destinationText}</RichTextContent>}
          {carrierText && <DangerousHtmlContent content={carrierText} />}
          <DangerousHtmlContent content={t('Destination.Text')} />
        </Stack>
      </PageBlock>
    </>
  );
};

DestinationPage.propTypes = {
  carrierCode: PropTypes.string,
  changeValues: PropTypes.func,
  countryCode: PropTypes.string,
  destination: SearchLocation,
  destinationTextId: PropTypes.string,
  hasLogo: PropTypes.bool,
  isRestricted: PropTypes.bool,
  origin: SearchLocation,
  setCarrierCode: PropTypes.func,
  setInitialDates: PropTypes.func,
  textCode: PropTypes.string,
};

const mapDispatchToProps = (dispatch) => ({
  changeValues: ({ origin, destination }) => {
    dispatch(change(SEARCH, `${SINGLE_BOUND}.${ORIGIN}`, origin));
    dispatch(change(SEARCH, `${SINGLE_BOUND}.${DESTINATION}`, destination));
    dispatch(change(SEARCH, `${MULTI_BOUND}[0].${ORIGIN}`, origin));
    dispatch(change(SEARCH, `${MULTI_BOUND}[0].${DESTINATION}`, destination));
  },
  setCarrierCode: (carrierCode) => {
    dispatch(change(SEARCH, `${CARRIER_CODES}`, [carrierCode]));
  },
  setInitialDates: () => {
    dispatch(change(SEARCH, `${SINGLE_BOUND}.${DEPARTURE_DATE}`, startOfTomorrow()));
    dispatch(change(SEARCH, `${SINGLE_BOUND}.${RETURN_DATE}`, addDays(startOfTomorrow(), 6)));
    dispatch(change(SEARCH, `${MULTI_BOUND}[0].${DEPARTURE_DATE}`, startOfTomorrow()));
  },
});

export default connect(null, mapDispatchToProps)(DestinationPage);
